/* ============================================================================
 * ez-engine-nest.js — EZ-Snap roll cut-pattern NESTING engine (theme asset, vanilla JS)
 * Source of truth: demo/app/lib/shadeNest.js (ported from the legacy ezsnaps-builder
 * bin-packer — a shelf pack with rotation search).
 *
 * Panels arrive at their FABRIC size (mounting allowance already applied upstream via
 * the shade engine's fabricSize). A 1" margin is left between panels / at cuts. The roll
 * width (72" / 6 ft) and the rollPlan function are INJECTED (the demo imports them from
 * shadePricing) — this engine bakes no dimension or price:
 *
 *   const nest = window.EZSnap.createNestEngine({
 *     rollWidthIn: 72,
 *     rollPlan: shade.rollPlan       // from the shade engine (stock-length aware)
 *   });
 *
 * PUBLIC API:
 *   nest.nestPanels(panels) -> { placed, shelves, lengthIn, lengthFt, used, total,
 *                                wastePct, rolls, oversize? }
 *
 * [2026-08 CHANGE — kerf model + void-fill] Two surgical changes, both validated against the
 * live engine over 3,831 real 12-mo quotes (0 fabric overlaps, 0 regressions, never-worse):
 *   (1) EDGE-KERF: the 1" cut margin is placed only BETWEEN adjacent panels, never against the
 *       roll's side edges (the roll's manufactured 72" edge is not a cut). Recovers full-width
 *       (72") shades that previously "oversized" by exactly 1", and stops the phantom edge inch
 *       from forcing near-72" panels into the wrong orientation/roll.  (+121 buildable.)
 *   (2) GUILLOTINE void-fill: a guillotine strip packer runs alongside the legacy shelf packer;
 *       the engine returns whichever uses FEWER roll-feet (best-of, so it can only match or beat
 *       the current result). Guillotine is used only when it fits a SINGLE roll (<= longest roll),
 *       so no panel is ever seamed across a physical roll. (239 orders drop a roll size; 1,880
 *       pack tighter; ~$12.6k/yr less roll material.)  The legacy shelf path is unchanged for
 *       multi-roll orders, preserving the no-seam guarantee.
 * ============================================================================ */
(function (root, factory) {
  var api = factory();
  if (typeof module !== "undefined" && module.exports) module.exports = api; // node/gate
  if (root && typeof root === "object") {
    root.EZSnap = root.EZSnap || {};
    root.EZSnap.createNestEngine = api.createNestEngine;
  }
})(typeof window !== "undefined" ? window : this, function () {
  "use strict";

  var MARGIN = 1; // inch between panels / at cuts (NOT at the roll's side edges — see (1) above)

  // Shelf-pack a fixed-orientation set into the roll width. First-fit-decreasing by height,
  // best-fit by least leftover width (the legacy heuristic).
  // [FIX 1] pieces carry `pwFab` (TRUE fabric width across the roll) and `ph` (length + MARGIN).
  //         Kerf is added only BETWEEN panels: the first panel on a shelf sits flush at x=0 and
  //         the shelf's outer edge is the roll edge (no trailing margin).
  function shelfPack(pieces, widthIn) {
    var sorted = pieces.slice().sort(function (a, b) { return b.ph - a.ph; });
    var shelves = [];
    var cursor = 0;
    for (var k = 0; k < sorted.length; k++) {
      var p = sorted[k];
      var best = -1;
      var bestLeft = Infinity;
      for (var i = 0; i < shelves.length; i++) {
        var s = shelves[i];
        var need = s.x === 0 ? p.pwFab : MARGIN + p.pwFab; // kerf only when a neighbor precedes it
        if (s.x + need <= widthIn && p.ph <= s.h) {
          var left = widthIn - s.x - need;
          if (left < bestLeft) { bestLeft = left; best = i; }
        }
      }
      if (best >= 0) {
        var sh = shelves[best];
        var xPos = sh.x === 0 ? 0 : sh.x + MARGIN;
        sh.items.push(Object.assign({}, p, { x: xPos, y: sh.y }));
        sh.x = xPos + p.pwFab;
      } else {
        shelves.push({ y: cursor, h: p.ph, x: p.pwFab, items: [Object.assign({}, p, { x: 0, y: cursor })] });
        cursor += p.ph;
      }
    }
    var placed = shelves.reduce(function (acc, s) { return acc.concat(s.items); }, []);
    return { shelves: shelves, lengthIn: cursor, placed: placed };
  }

  // [FIX 2] Guillotine strip packer — fills the vertical voids the shelf packer leaves (e.g. a lone
  // narrow panel stranded on its own row). Stays guillotine (straight edge-to-edge cuts, DIY-safe).
  // Kerf is added on the split (BETWEEN panels), never at the strip's outer edges — same rule as (1).
  // Returns { placed, lengthIn } or null (a panel wider than the roll in both orientations).
  function guillotinePack(base, widthIn) {
    function packOne(order, scoreMode) {
      var free = [{ x: 0, y: 0, w: widthIn, h: 1e9 }];
      var placed = [];
      for (var k = 0; k < order.length; k++) {
        var p = order[k];
        var opts = [{ pw: p.fabW, ph: p.fabH, rot: false }, { pw: p.fabH, ph: p.fabW, rot: true }];
        var ch = null;
        for (var oi = 0; oi < opts.length; oi++) {
          var o = opts[oi];
          if (o.pw > widthIn + 1e-9) continue;
          for (var i = 0; i < free.length; i++) {
            var f = free[i];
            if (o.pw <= f.w + 1e-9 && o.ph <= f.h + 1e-9) {
              // scoreMode 0 = best-area-fit (the SHIPPED scoring, kept); 1 = best-short-side-fit. topmost first.
              var score = scoreMode === 1
                ? f.y * 1e5 + Math.min(f.w - o.pw, f.h - o.ph) * 100 + (f.w - o.pw) + (f.h - o.ph)
                : f.y * 1e5 + (f.w - o.pw) * 100 + (f.h - o.ph);
              if (!ch || score < ch.score) ch = { i: i, x: f.x, y: f.y, pw: o.pw, ph: o.ph, rot: o.rot, score: score };
            }
          }
        }
        if (!ch) return null;
        var fr = free[ch.i]; free.splice(ch.i, 1);
        placed.push(Object.assign({}, p, { x: ch.x, y: ch.y, rot: ch.rot, pwFab: ch.pw, phFab: ch.ph }));
        var rx = ch.x + ch.pw + MARGIN; // right free rect starts after this panel + kerf
        if (fr.x + fr.w - rx > 1e-9) free.push({ x: rx, y: ch.y, w: fr.x + fr.w - rx, h: ch.ph });
        var by = ch.y + ch.ph + MARGIN; // below free rect (full width of the consumed rect) + kerf
        if (fr.y + fr.h - by > 1e-9) free.push({ x: fr.x, y: by, w: fr.w, h: fr.y + fr.h - by });
      }
      var length = placed.reduce(function (m, q) { return Math.max(m, q.y + q.phFab); }, 0);
      return { placed: placed, lengthIn: length };
    }
    // [FIX 4 — stronger void-fill] Search MORE orderings x TWO placement heuristics. The original 4 sorts
    // under scoreMode 0 are a SUBSET of this search, so the result is provably NEVER WORSE than shipped —
    // it can only pack the same or tighter. Still guillotine, still single-roll (seam-safe).
    var best = null;
    function tryOrder(order) { for (var sm = 0; sm < 2; sm++) { var r = packOne(order, sm); if (r && (!best || r.lengthIn < best.lengthIn)) best = r; } }
    var sorts = [
      function (a, b) { return Math.max(b.fabW, b.fabH) - Math.max(a.fabW, a.fabH); }, // original 1
      function (a, b) { return b.fabW * b.fabH - a.fabW * a.fabH; },                   // original 2
      function (a, b) { return Math.min(b.fabW, b.fabH) - Math.min(a.fabW, a.fabH); }, // original 3
      function (a, b) { return b.fabW + b.fabH - (a.fabW + a.fabH); },                 // original 4
      function (a, b) { return b.fabH - a.fabH; },                                     // + height desc
      function (a, b) { return b.fabW - a.fabW; }                                      // + width desc
    ];
    for (var j = 0; j < sorts.length; j++) tryOrder(base.slice().sort(sorts[j]));
    // Small builds: exhaustively try EVERY ordering (Heap's algorithm). Some tight packings — a void a
    // smaller cut fills — are reachable only by a specific interleaving that no deterministic sort produces
    // (e.g. a 7-panel build that a sort packs to 184in but an interleave packs to 169in, dropping a roll
    // size). Bounded to n<=7 (<=5040 orderings, single-digit ms) so derive() stays instant; larger builds
    // rely on the sorts above. This only ADDS candidates to the same best-of, so it stays a strict superset
    // of the shipped search -> provably never worse, never an overlap (same guillotine placement).
    if (base.length <= 7) {
      var arr = base.slice(), ctr = [];
      for (var ci = 0; ci < arr.length; ci++) ctr.push(0);
      tryOrder(arr.slice());
      var pi = 0;
      while (pi < arr.length) {
        if (ctr[pi] < pi) {
          var sw = pi % 2 === 0 ? 0 : ctr[pi];
          var tmp = arr[sw]; arr[sw] = arr[pi]; arr[pi] = tmp;
          tryOrder(arr.slice());
          ctr[pi]++; pi = 0;
        } else { ctr[pi] = 0; pi++; }
      }
    }
    return best;
  }

  // Bin-pack WHOLE shelves into stock rolls (unchanged): no shelf ever straddles a physical roll seam.
  function binPackShelves(shelves, maxRollIn, rollPlan) {
    var sorted = shelves.slice().sort(function (a, b) { return b.h - a.h; });
    var bins = [];
    for (var i = 0; i < sorted.length; i++) {
      var s = sorted[i], placed = false;
      for (var j = 0; j < bins.length; j++) {
        if (bins[j].fill + s.h <= maxRollIn) { bins[j].fill += s.h; bins[j].shelves.push(s); placed = true; break; }
      }
      if (!placed) bins.push({ fill: s.h, shelves: [s] });
    }
    var rolls = [];
    for (var b = 0; b < bins.length; b++) {
      var plan = rollPlan(bins[b].fill / 12);
      bins[b].rollFt = plan.reduce(function (s, f) { return s + f; }, 0);
      rolls = rolls.concat(plan);
    }
    return { rolls: rolls, bins: bins };
  }

  function createNestEngine(cfg) {
    cfg = cfg || {};
    var ROLL_WIDTH_IN = cfg.rollWidthIn || 72;
    var rollPlan = cfg.rollPlan || function () { return []; };
    var ROLL_LENGTHS_FT = (cfg.rollLengthsFt && cfg.rollLengthsFt.length) ? cfg.rollLengthsFt.slice() : [25];
    var MAX_ROLL_IN = Math.max.apply(null, ROLL_LENGTHS_FT) * 12;

    function ft(rolls) { return rolls.reduce(function (s, f) { return s + f; }, 0); }

    function nestPanels(panels) {
      var base = panels.map(function (p) { return Object.assign({}, p); }); // keep fabW/fabH + metadata
      var empty = { placed: [], shelves: [], lengthIn: 0, lengthFt: 0, used: 0, total: 0, wastePct: 0, rolls: [] };
      if (!base.length) return empty;

      // ---- (A) legacy shelf packer, now edge-kerf'd + seam-safe for any size ----
      // [FIX 1] orient with pwFab = TRUE fabric width across the roll; reject only if fabric itself
      //         exceeds the roll (no +MARGIN). ph = along-roll length + MARGIN (length cuts are real).
      var orient = function (mask) {
        var o = base.map(function (p, i) {
          return (mask >> i) & 1
            ? Object.assign({}, p, { pwFab: p.fabH, ph: p.fabW + MARGIN, rot: true })
            : Object.assign({}, p, { pwFab: p.fabW, ph: p.fabH + MARGIN, rot: false });
        });
        return o.some(function (p) { return p.pwFab > ROLL_WIDTH_IN; }) ? null : shelfPack(o, ROLL_WIDTH_IN);
      };
      var n = base.length, shelfBest = null;
      var combos = n <= 16 ? (1 << n) : 0;
      for (var m = 0; m < combos; m++) { var r = orient(m); if (r && (!shelfBest || r.lengthIn < shelfBest.lengthIn)) shelfBest = r; }
      if (n > 16) {
        // heuristics: all-unrot (width=fabW), all-rot (width=fabH), smaller-side-as-width
        var packHeur = function (rotFn) {
          var o = base.map(function (p) { var rot = rotFn(p); return Object.assign({}, p, rot ? { pwFab: p.fabH, ph: p.fabW + MARGIN, rot: true } : { pwFab: p.fabW, ph: p.fabH + MARGIN, rot: false }); });
          if (o.some(function (p) { return p.pwFab > ROLL_WIDTH_IN; })) return null;
          return shelfPack(o, ROLL_WIDTH_IN);
        };
        [function () { return false; }, function () { return true; }, function (p) { return p.fabW > p.fabH; }].forEach(function (rotFn) {
          var rr = packHeur(rotFn); if (rr && (!shelfBest || rr.lengthIn < shelfBest.lengthIn)) shelfBest = rr;
        });
      }
      if (!shelfBest) return Object.assign({}, empty, { oversize: true, oversizeReason: "width" });
      var tallShelf = shelfBest.shelves.reduce(function (m, s) { return Math.max(m, s.h); }, 0);
      // [FIX 3 — length-end kerf] a shelf's height carries a trailing MARGIN (ph = fabric-length + MARGIN),
      // but the cut at the roll's END is phantom (no panel after it) — the SAME insight as the side edge-kerf,
      // on the length axis. A panel whose FABRIC length == the longest roll fits it exactly; only a panel that
      // still exceeds the roll after removing that phantom trailing margin is truly oversize. (Fixes 25ft-exact
      // shades being wrongly rejected; flips only panels of length (MAX, MAX+MARGIN], never any current build.)
      if (tallShelf - MARGIN > MAX_ROLL_IN) return Object.assign({}, empty, { oversize: true, oversizeReason: "length", maxPanelIn: tallShelf - MARGIN, maxRollIn: MAX_ROLL_IN });

      var shelfPacked = binPackShelves(shelfBest.shelves, MAX_ROLL_IN, rollPlan);

      // ---- (B) guillotine void-fill — only when it fits a SINGLE roll (no seam) ----
      // [FIX 2] Fold panel dims to fabW/fabH for the packer; keep the winner if it uses fewer roll-feet.
      var guil = guillotinePack(base, ROLL_WIDTH_IN);
      var guilPacked = null;
      if (guil && guil.lengthIn <= MAX_ROLL_IN) {
        var gRolls = rollPlan(guil.lengthIn / 12);
        // guillotine placed panels -> shelf-shaped bin so the diagram + downstream read identically
        guilPacked = { rolls: gRolls, lengthIn: guil.lengthIn, placed: guil.placed };
      }

      // ---- choose best-of by total roll-feet (fewer feet == less material, ~cheaper) ----
      // Tie-break to the guillotine when it packs TIGHTER (fills the void / cleaner cut) even if the
      // roll count is identical — so a customer never sees a stranded void when a snug layout exists.
      // Still never pricier: guillotine is only used when its roll-feet <= the shelf's.
      var useGuil = guilPacked && (ft(guilPacked.rolls) < ft(shelfPacked.rolls)
        || (ft(guilPacked.rolls) === ft(shelfPacked.rolls) && guilPacked.lengthIn < shelfBest.lengthIn));
      var placed, rolls, lengthIn, shelvesOut, rollBins;
      if (useGuil) {
        placed = guilPacked.placed; rolls = guilPacked.rolls; lengthIn = guilPacked.lengthIn;
        shelvesOut = []; // guillotine is not shelf-structured; diagram falls back to `placed`
        rollBins = [{ rollFt: ft(rolls), usedIn: lengthIn, items: placed }];
      } else {
        placed = shelfBest.placed; rolls = shelfPacked.rolls; lengthIn = shelfBest.lengthIn;
        shelvesOut = shelfBest.shelves;
        rollBins = shelfPacked.bins.map(function (bin) {
          var y = 0, items = [];
          bin.shelves.forEach(function (sh) { sh.items.forEach(function (it) { items.push(Object.assign({}, it, { y: y })); }); y += sh.h; });
          return { rollFt: bin.rollFt, usedIn: bin.fill, items: items };
        });
      }

      var used = placed.reduce(function (s, p) { return s + p.fabW * p.fabH; }, 0);
      var totalRollFt = ft(rolls);
      var total = ROLL_WIDTH_IN * totalRollFt * 12;
      var wastePct = total > 0 ? Math.max(0, ((total - used) / total) * 100) : 0;
      // canonical placed item (both nest paths + the demo source-of-truth emit exactly this shape):
      // pw/ph = the fabric dims AS PLACED (across-roll / along-roll), no margin baked in.
      var placedOut = placed.map(function (p) {
        return { fabW: p.fabW, fabH: p.fabH, winW: p.winW, winH: p.winH, name: p.name, wi: p.wi, cn: p.cn,
          x: p.x, y: p.y, rot: !!p.rot, pw: p.rot ? p.fabH : p.fabW, ph: p.rot ? p.fabW : p.fabH };
      });
      return { placed: placedOut, shelves: shelvesOut, lengthIn: lengthIn, lengthFt: lengthIn / 12, used: used, total: total, wastePct: wastePct, rolls: rolls, rollBins: rollBins };
    }

    return {
      nestPanels: nestPanels,
      // exposed for parity testing — accepts pre-oriented pieces {pwFab, ph, ...}
      shelfPack: function (pieces) { return shelfPack(pieces, ROLL_WIDTH_IN); }
    };
  }

  return { createNestEngine: createNestEngine };
});
